import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import msk from "msk";
import { useRecoilValue } from "recoil";

const Wrapper = styled.div`
  max-width: 400px;
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9;
`;

const InputContainer = styled.div`
  width: 100%;
  height: 60px;

  margin-top: 10px;
  margin-bottom: 10px;
`;

const SingleInputContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-evenly;

  > input + input {
    margin-left: 15px;
  }
`;

const SingleInput = styled.input<{ error: string }>`
  width: 60px;
  height: 70px;
  padding: 8px;
  font-size: 24px;
  appearance: none;
  border: 1px solid ${(props) => (props.error ? "#C00000" : "#cccccc")};
  border-radius: 5px;
  background-color: ${(props) => (props.error ? "#FAEBEB" : "#ffffff")};
  color: #2f2f2f;
  text-align: center;

  :focus {
    outline: none;
  }
`;

const ErrorText = styled.div`
  margin-top: -35px;
  margin-left: 22px;
  color: #c00000;
  size: 14px;

  @media (max-width: 740px) {
    margin-left: 12px;
  }

  @media (max-width: 650px) {
    margin-left: 5px;
  }
`;

const NextButtonContainer = styled.div`
  width: 100%;
  margin-top: 50px;
`;

const LinkContainer = styled.div`
  width: 100%;
  margin-top: 40px;
  text-align: center;

  @media (max-width: 1152px) {
    margin-top: 32px;
  }

  @media (max-width: 768px) {
    margin-top: 24px;
  }
`;

const CustomLink = styled.a`
  font-weight: 600;
  font-size: 16px;
  color: #fb972e;
  cursor: pointer;

  :hover {
    text-decoration: underline;
  }

  @media (max-width: 576px) {
    font-size: 14px;
  }
`;
interface Props {
  onChange: (password: string) => void;
}

export default function GenericPasswordInput(props: Props): JSX.Element {
  const { onChange } = props;

  const [error, setError] = useState<string>("");

  const inputs = [
    useRef<HTMLInputElement | null>(null),
    useRef<HTMLInputElement | null>(null),
    useRef<HTMLInputElement | null>(null),
    useRef<HTMLInputElement | null>(null),
  ];

  const [password, setPassword] = useState<string[]>(["", "", "", ""]);

  useEffect(() => {
    onChange(password.join(""));
  }, [password]);

  useEffect(() => {
    inputs[0].current?.focus();
    // I just want this to run at the first time
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handlePasswordKeyPressed(
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) {
    const pass = [...password];
    if (index <= 3 && index >= -1) {
      switch (event.key) {
        case "Backspace":
          if (pass[index] === "") pass[index - 1] = "";
          else pass[index] = "";
          setPassword(pass);
          inputs[index - 1]?.current?.focus();
          return;
        case "ArrowLeft":
          inputs[index - 1]?.current?.focus();
          return;

        case "ArrowRight":
          inputs[index + 1]?.current?.focus();
          return;

        default:
          // If it's a number
          if (event.key.charCodeAt(0) >= 48 && event.key.charCodeAt(0) <= 57) {
            const maskedValue = msk.fit(event.key, "S");

            pass[index] = maskedValue;
            setPassword(pass);

            if (index < 3 && index >= -1) inputs[index + 1].current?.focus();
            setError("");
          }

          break;
      }
    }
  }

  return (
    <>
      <Wrapper>
        <InputContainer>
          <SingleInputContainer>
            {inputs.map((_, index) => (
              <SingleInput
                // Sometimes index is a bad practice but here it's static
                // so it's a pass. I know what I'm doing
                onKeyDown={(evt) => handlePasswordKeyPressed(evt, index)}
                // eslint-disable-next-line react/no-array-index-key
                key={`input_${index}`}
                onChange={() => {}}
                error={error}
                ref={inputs[index]}
                type="password"
                value={password[index]}
                pattern="[0-9]*"
                inputMode="numeric"
              />
            ))}
          </SingleInputContainer>
          {error && (
            <NextButtonContainer>
              <ErrorText>{error}</ErrorText>
            </NextButtonContainer>
          )}
        </InputContainer>
      </Wrapper>
    </>
  );
}
